const DEFAULT_FIELDS = [
  {
    key: 'query',
    label: 'What should the report analyze?',
    type: 'long_text',
    required: true,
    placeholder: 'Describe the situation you want analyzed...',
    max_length: 2000,
  },
];

const DEFAULT_SECTIONS = [
  { key: 'report', label: 'Report', source: 'answer' },
];

const FIELD_TYPES = new Set(['text', 'long_text', 'select', 'radio', 'date', 'number', 'hidden']);

function asArray(value, fallback = []) {
  return Array.isArray(value) ? value : fallback;
}

function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function normalizeField(field, index) {
  const normalized = asObject(field);
  const type = FIELD_TYPES.has(normalized.type) ? normalized.type : 'text';
  const key = normalized.key || `field_${index + 1}`;
  return {
    key,
    label: normalized.label || key,
    type,
    required: Boolean(normalized.required),
    placeholder: normalized.placeholder || '',
    default: normalized.default ?? '',
    maxLength: Number(normalized.max_length || normalized.maxLength || 2000),
    options: asArray(normalized.options).map((option) => {
      if (typeof option === 'string') {
        return { value: option, label: option };
      }
      return {
        value: String(option.value ?? option.label ?? ''),
        label: String(option.label ?? option.value ?? ''),
      };
    }).filter((option) => option.value),
  };
}

function normalizeSection(section, index) {
  const normalized = asObject(section);
  const key = normalized.key || `section_${index + 1}`;
  return {
    key,
    label: normalized.label || key,
    source: normalized.source || 'answer',
    emptyState: normalized.empty_state || normalized.emptyState || '',
  };
}

export function resolveStructuredReportConfig(generatorConfig = {}, app = {}) {
  const config = asObject(generatorConfig);
  const input = asObject(config.input);
  const result = asObject(config.result);
  const access = asObject(config.access);
  const share = asObject(config.share);
  const theme = asObject(config.theme);
  const fields = asArray(input.fields, DEFAULT_FIELDS).map(normalizeField);

  return {
    input: {
      submitLabel: input.submit_label || input.submitLabel || 'Generate report',
      fields: fields.length > 0 ? fields : DEFAULT_FIELDS.map(normalizeField),
      examples: asArray(input.examples, asArray(config.examples, [])),
    },
    result: {
      title: result.title || 'Personalized report',
      sections: asArray(result.sections, DEFAULT_SECTIONS).map(normalizeSection),
      fallbackSource: result.fallback_source || result.fallbackSource || 'answer',
    },
    access: {
      guestQuota: {
        enabled: access.guest_quota?.enabled ?? true,
        limit: access.guest_quota?.limit ?? 3,
        period: access.guest_quota?.period ?? 'day',
      },
      auth: {
        enabled: Boolean(access.auth?.enabled),
        cta: access.auth?.cta || 'Sign in to save and continue',
      },
      payment: {
        enabled: Boolean(access.payment?.enabled),
        cta: access.payment?.cta || 'Unlock full report',
      },
    },
    share: {
      title: share.title || app.appName || 'AI report',
      description: share.description || app.appDescription || '',
      cta: share.cta || 'Try your own report',
      copyText: (share.copy_template || share.copyTemplate || 'I generated a report with Dream Lens Dogfood.')
        .replace(/\{\{APP_NAME\}\}/g, app.appName || 'this app'),
    },
    theme: {
      accentColor: theme.accent_color || theme.accentColor || '#2563eb',
      background: theme.background || '#f8fafc',
      surface: theme.surface || '#ffffff',
      text: theme.text || '#111827',
      radius: theme.radius || '8px',
    },
  };
}

export function buildReportRequestPayload(values, fields) {
  const normalizedValues = Object.fromEntries(
    Object.entries(values || {})
      .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
      .filter(([, value]) => value !== '' && value !== undefined && value !== null),
  );
  const visibleFields = fields.filter((field) => field.type !== 'hidden');
  const primaryField =
    visibleFields.find((field) => field.type === 'long_text') ||
    visibleFields.find((field) => field.required) ||
    visibleFields[0];
  const query = String(normalizedValues[primaryField?.key] || Object.values(normalizedValues)[0] || '').trim();
  return {
    displayPrompt: query,
    runtimeInput: {
      query,
      inputs: {
        ...normalizedValues,
        query,
      },
    },
  };
}

function readPath(value, path) {
  return path.split('.').reduce((current, part) => {
    if (current && typeof current === 'object' && part in current) {
      return current[part];
    }
    return undefined;
  }, value);
}

export function getReportSections(result, sections) {
  return sections
    .map((section) => ({
      key: section.key,
      label: section.label,
      content: readPath(result, section.source) ?? section.emptyState,
    }))
    .filter((section) => section.content !== undefined && section.content !== null && String(section.content).trim() !== '');
}
